/**
 * Cheap CRM presence rollups: increment one daily + one monthly doc per flush.
 * Admin reads are on-demand getDocs — never a global CRMContext subscription.
 */
import {
  collection,
  doc,
  getDocs,
  increment,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  type CrmActivitySlice,
  type CrmHoursDoc,
  type CrmHoursPeriod,
  CRM_HOURS_FALLBACK_TZ,
  crmHoursPeriodRange,
  dailyDocId,
  monthlyDocId,
  resolveCrmActivityTimezone,
} from './crmActivityHours';

export const CRM_ACTIVITY_DAILY = 'userCrmActivityDaily';
export const CRM_ACTIVITY_MONTHLY = 'userCrmActivityMonthly';

export async function incrementCrmActivity(
  userId: string,
  timezone: string,
  slices: CrmActivitySlice[],
): Promise<void> {
  if (!userId || slices.length === 0) return;
  const tz = resolveCrmActivityTimezone(timezone);
  const batch = writeBatch(db);
  let writes = 0;

  for (const slice of slices) {
    const inH = Math.round(slice.minutesInHours * 100) / 100;
    const after = Math.round(slice.minutesAfterHours * 100) / 100;
    if (inH <= 0 && after <= 0) continue;

    const dailyRef = doc(db, CRM_ACTIVITY_DAILY, dailyDocId(userId, slice.dateKey));
    batch.set(dailyRef, {
      userId,
      dateKey: slice.dateKey,
      timezone: tz,
      minutesInHours: increment(inH),
      minutesAfterHours: increment(after),
      updatedAt: serverTimestamp(),
    }, { merge: true });

    const monthlyRef = doc(db, CRM_ACTIVITY_MONTHLY, monthlyDocId(userId, slice.monthKey));
    batch.set(monthlyRef, {
      userId,
      monthKey: slice.monthKey,
      timezone: tz,
      minutesInHours: increment(inH),
      minutesAfterHours: increment(after),
      updatedAt: serverTimestamp(),
    }, { merge: true });

    writes += 2;
  }

  if (writes === 0) return;
  await batch.commit();
}

function mapHoursDoc(data: Record<string, unknown>, fallbackId: string): CrmHoursDoc {
  return {
    userId: typeof data.userId === 'string' ? data.userId : fallbackId,
    minutesInHours: typeof data.minutesInHours === 'number' ? data.minutesInHours : 0,
    minutesAfterHours: typeof data.minutesAfterHours === 'number' ? data.minutesAfterHours : 0,
    timezone: typeof data.timezone === 'string' ? data.timezone : undefined,
  };
}

export async function fetchCrmHoursDocs(
  period: CrmHoursPeriod,
  now: Date = new Date(),
): Promise<CrmHoursDoc[]> {
  if (period === 'all') {
    const snap = await getDocs(collection(db, CRM_ACTIVITY_MONTHLY));
    return snap.docs.map((d) => mapHoursDoc(d.data() as Record<string, unknown>, d.id));
  }

  const range = crmHoursPeriodRange(period, now, CRM_HOURS_FALLBACK_TZ);

  if (period === 'year') {
    const q = query(
      collection(db, CRM_ACTIVITY_MONTHLY),
      where('monthKey', '>=', range.startMonthKey),
      where('monthKey', '<=', range.endMonthKey),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => mapHoursDoc(d.data() as Record<string, unknown>, d.id));
  }

  const q = query(
    collection(db, CRM_ACTIVITY_DAILY),
    where('dateKey', '>=', range.startDateKey),
    where('dateKey', '<=', range.endDateKey),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => mapHoursDoc(d.data() as Record<string, unknown>, d.id));
}
